/**
 * Path grooves on the host side (contract v66): sorting the Design list into
 * plain grooves, preset groups and hand-drawn paths, and regenerating a
 * bypass-shoulder group from its sliders.
 *
 * It lives beside the CNC export because the bypass shoulder IS the shop
 * sample's end-mill motif — the preset exists so the builder can draw what the
 * machine cuts. The Joints tab is the only caller; `scripts/check-cnc-ui.ts`
 * runs it headless.
 *
 * Every edit returns a whole new Design list for `setDesignGrooves`. A preset
 * group is always rebuilt through the contract's own `bypassShoulderGrooves`,
 * never patched groove by groove: the five grooves share one geometry, and
 * the generator is the single place that knows how.
 */
import { BYPASS_SHOULDER_DEFAULTS, bypassShoulderGrooves } from "../../../shared/api-contract";
import type { BypassShoulderParams, DesignGroove } from "../../../shared/api-contract";

export const isPathGroove = (g: DesignGroove) => g.direction === "path";

/** The grooves the numbered chips edit: around and across, never a path. */
export function plainGrooves(list: readonly DesignGroove[]): DesignGroove[] {
  return list.filter((g) => !isPathGroove(g));
}

/** Path grooves no preset made — shown as compact rows, width and depth only. */
export function customPaths(list: readonly DesignGroove[]): DesignGroove[] {
  return list.filter((g) => isPathGroove(g) && !g.preset);
}

export interface ShoulderGroup {
  group: string;
  /** The params the group was last generated from (its first groove's). */
  params: BypassShoulderParams;
  ids: string[];
}

/** Every bypass-shoulder group in the list, in the order they first appear. */
export function shoulderGroups(list: readonly DesignGroove[]): ShoulderGroup[] {
  const out: ShoulderGroup[] = [];
  for (const g of list) {
    if (!isPathGroove(g) || g.preset?.kind !== "bypassShoulder") continue;
    const found = out.find((s) => s.group === g.preset!.group);
    if (found) found.ids.push(g.id);
    else out.push({ group: g.preset.group, params: { ...g.preset.params }, ids: [g.id] });
  }
  return out;
}

/** A group name no groove id in `list` already starts with: "bypass", "bypass2", … */
export function newShoulderGroup(list: readonly DesignGroove[]): string {
  const taken = (name: string) => list.some((g) => g.id === name || g.id.startsWith(`${name}-`) || g.preset?.group === name);
  if (!taken("bypass")) return "bypass";
  for (let n = 2; ; n++) if (!taken(`bypass${n}`)) return `bypass${n}`;
}

/**
 * The params a new shoulder starts from: the sample's, with the motif
 * reaching across THIS band (`spanMM` is the band's own width, the
 * generator's documented normal case).
 */
export function shoulderStartParams(bandWidthMM: number): BypassShoulderParams {
  return { ...BYPASS_SHOULDER_DEFAULTS, spanMM: Math.min(12, Math.max(1, bandWidthMM)) };
}

/** The list with a new shoulder group appended. */
export function addShoulder(list: readonly DesignGroove[], params: Partial<BypassShoulderParams>, group: string): DesignGroove[] {
  return [...list, ...bypassShoulderGrooves(params, group)];
}

/**
 * The list with `group` regenerated from `params`, in the place its first
 * groove held (so the list order, and every other groove, is untouched).
 * Unknown group ⇒ the list unchanged.
 */
export function regenerateShoulder(list: readonly DesignGroove[], group: string, params: Partial<BypassShoulderParams>): DesignGroove[] {
  const mine = (g: DesignGroove) => isPathGroove(g) && g.preset?.kind === "bypassShoulder" && g.preset.group === group;
  const at = list.findIndex(mine);
  if (at < 0) return [...list];
  const rest = list.filter((g) => !mine(g));
  const before = list.slice(0, at).filter((g) => !mine(g)).length;
  return [...rest.slice(0, before), ...bypassShoulderGrooves(params, group), ...rest.slice(before)];
}

/** The list without `group`'s grooves. */
export function removeShoulder(list: readonly DesignGroove[], group: string): DesignGroove[] {
  return list.filter((g) => !(isPathGroove(g) && g.preset?.group === group));
}

/** One shoulder slider: the range is the generator's own clamp, so no stretch of it is dead. */
export interface ShoulderSlider {
  key: keyof BypassShoulderParams;
  label: string;
  min: number;
  /** A function where the bound moves with another param (inset ≤ span / 2). */
  max: number | ((p: BypassShoulderParams) => number);
  step: number;
  unit: string;
  hint: string;
}

export const SHOULDER_SLIDERS: readonly ShoulderSlider[] = [
  { key: "angleDeg", label: "Step Angle", min: 10, max: 90, step: 1, unit: "°",
    hint: "How steeply the step crosses the band — 90° goes straight across." },
  { key: "offsetMM", label: "Distance From Centre", min: 0, max: 20, step: 0.05, unit: "mm",
    hint: "How far each step stands from the middle of the design, measured round the ring." },
  { key: "spanMM", label: "Reach Across", min: 1, max: 12, step: 0.05, unit: "mm",
    hint: "How far across the band the shoulder reaches — normally the band's own width." },
  { key: "insetMM", label: "Track Inset", min: 0, max: (p) => p.spanMM / 2, step: 0.05, unit: "mm",
    hint: "How far the grooves running round the ring sit in from the edge." },
  { key: "widthMM", label: "Groove Width", min: 0.25, max: 3, step: 0.05, unit: "mm",
    hint: "One pass of the cutter — 1.3 mm in the shop's sample." },
  { key: "depthMM", label: "Groove Depth", min: 0.1, max: 1, step: 0.05, unit: "mm", hint: "" },
  { key: "cornerRadiusMM", label: "Corner Rounding", min: 0, max: 5, step: 0.05, unit: "mm",
    hint: "The curve where the track turns into the step." },
];

export const sliderMax = (s: ShoulderSlider, p: BypassShoulderParams) => (typeof s.max === "function" ? s.max(p) : s.max);
